// #region Imports
import { defineComponent } from "vue";
import ProductSelectorOption from "../models/ProductSelector.Option";
import ProductSelectorValue from "../models/ProductSelector.Value";
// #endregion
// #region Core
export default defineComponent({
    // #region Vars
    name: 'g-product-selector',
    emits: ["command"],
    // #endregion
    // #region Properties
    props: {
        product: {
            type: Object,
            required: false,
            default: {}
        },
        itemNumber: {
            type: String,
            required: false,
            default: ''
        },
        entryData: {
            type: Object,
            required: false,
            default: {}
        },
    },
    data: () => ({
        items: [],
        currentItem: undefined,
        currentNumber: '',
        options: {},
        selectedItems: [],
        _isWorking: false
    }),
    // #endregion
    // #region Constructor
    created() {
        //Load
        if (this.product) {
            this.SetProduct(this.product);
        }
    },
    // #endregion
    // #region Watch
    watch: {
        'product': function (value) {
            this.SetProduct(value);
        },
        'entryData.selection': {
            handler: function (value) {
                this.OnCustomItemChanged();
            },
            deep: true
        },
        'currentNumber': function (value) {
            if (!this._isWorking) {
                this.setCurrentItemByNumber(value);
            }
        },
        'itemNumber': function (value) {
            if (!this._isWorking && this.currentNumber !== this.itemNumber) {
                this.setCurrentItemByNumber(value);
            }
        },
    },
    // #endregion
    // #region Core
    methods: {
        // #region Selector
        SetProduct(product) {
            // #region Init
            //Lock
            this._isWorking = true;
            //System
            this.items = product.variants ?? [];
            this.currentItem = undefined;
            this.currentNumber = '';
            //Lock
            this._isWorking = false;
            //Options
            let tData = {};
            // #endregion
            // #region Build-Data
            if (utils.isNullOrEmpty(product)) {
                tData = {};
            }
            else {
                var tIndex = 0;
                product.selection.forEach((e) => {
                    //Member
                    var tMember = e.member;
                    //Options
                    tData[tMember] = new ProductSelectorOption(this, {
                        name: e.member,
                        caption: e.caption,
                        infoLink: e.infoLink,
                        infoText: e.infoText,
                        allowMultible: false,
                        isSelection: e.isSelection,
                        isFilterable: e.isFilterable,
                        controlType: e.controlType,
                        controlAttributes: e.controlAttributes,
                        initialValue: e.initialValue,
                        member: e.member,
                        index: tIndex,
                        currentValue: null,
                        currentValues: [],
                        values: this.getValues(e.values),
                    });
                    tIndex = tIndex + 1;
                });
            }
            //Set
            this.options = tData;
            this.entryData.selection = {};
            this.entryData.custom = {};
            // #endregion
            // #region Initial
            Object.keys(this.options).forEach(e => {
                if (!utils.isNullOrEmpty(this.options[e].initialValue)) {
                    this.options[e].currentValue = this.options[e].initialValue;
                }
            });
            // #endregion
            // #region Varaints
            if (utils.isNullOrEmpty(this.currentItem)) {
                if (product.variants.length != 1) {
                    this.setCurrentItem(product, this.product?.variants ?? []);
                }
                else {
                    this.setCurrentItem(product.variants[0], [product.variants[0]]);
                }
            }
            // #endregion
        },
        setCurrentItem(item, selectedItems) {
            // #region Selection
            this.selectedItems = selectedItems;
            // #endregion
            // #region Check
            if (!(!this.currentItem || !item || this.currentItem.itemNumber !== item.itemNumber)) {
                return;
            }
            // #endregion
            // #region Set
            this.currentItem = item;
            // #endregion                                
            // #region Selections
            Object.keys(this.options).forEach(e => {
                if (this.options[e].member && this.options[e].isSelection) {
                    //Find available values
                    let tValue = this.getValuesFromItems(selectedItems, e);
                    if (tValue.length == 1) {
                        this.options[e].currentValue = tValue[0];
                    }
                }
            });
            // #endregion                
            // #region Simple Events
            //Default
            this.OnCurrentItemChanged();
            this.OnCurrentImageChanged();
            //Run
            if (this.hasConfiguration()) {
                if (this.currentItem && this.currentItem.isReal) {
                    this.OnCustomItemChanged();
                }
            }
            // #endregion
        },
        getConfigurationValue(name) {
            // #region Optimize
            if (!this.entryData.selection) {
                this.entryData.selection = {};
            }
            if (!this.entryData.selection[name]) {
                this.entryData.selection[name] = {};
            }
            if (!this.entryData.selection[name].entry) {
                this.entryData.selection[name].entry = {};
            }
            // #endregion
            return this.entryData.selection[name].entry;
        },
        // #endregion
        // #region Selector | Events
        OnValueChange(object, member, oldValue, newValue) {
            // #region Selection
            if (!object.isSelection) {
                if (utils.isNullOrEmpty(newValue)) {
                    delete this.entryData.custom[object.name];
                }
                else {
                    this.entryData.custom[object.name] = newValue;
                }
            }
            else {
                if (utils.isNullOrEmpty(newValue)) {
                    delete this.entryData.selection[object.name];
                }
                else {
                    this.entryData.selection[object.name] = { value: newValue };
                }
            }
            if (!object.isSelection) {
                this.OnCurrentImageChanged();
                return;
            }
            // #endregion
            // #region Update-Selection
            if (!utils.isNullOrEmpty(object.values) && object.values.length > 0) {
                object.values.forEach(e => {
                    if (utils.isArray(newValue)) {
                        e.selected = newValue.some(f => f === e.name);
                    }
                    else {
                        e.selected = (e.name === newValue);
                    }
                });
            }
            // #endregion
            // #region Selection
            if (object.isSelection) {
                // #region Reset
                //Check, if has Items
                let tItems0 = this.getItemsByFilter(this.items, this.entryData.selection);
                let tStop = false;
                //Check filter
                if (tItems0.length === 0) {
                    Object.keys(this.entryData.selection).slice().reverse().forEach(e => {
                        if (tStop) {
                            //Do nothing
                        }
                        else if (e === object.name) {
                            //Do nothing
                        }
                        else {
                            //Clone-Filter for check
                            let tFilter = utils.cloneDeep(this.entryData.selection);
                            //Delete
                            delete tFilter[e];
                            //HasItems
                            var tHasItems = this.getItemsByFilter(this.items, tFilter).length > 0;
                            if (tHasItems) {
                                this.options[e].currentValue = null;
                                tStop = true;
                            }
                        }
                    });
                }
                // #endregion
                // #region Update
                Object.keys(this.options).forEach(e => {
                    if (this.options[e].member && this.options[e].isFilterable)
                        this.options[e].values.forEach(o => {
                            //Clone-Filter for check
                            let tFilter = utils.cloneDeep(this.entryData.selection);
                            //Set
                            tFilter[this.options[e].member] = { value: o.name };
                            //Set
                            o.possible = this.getItemsByFilter(this.items, tFilter).length > 0;
                        });
                });
                // #endregion
                // #region Reset | UserData
                Object.keys(this.entryData.custom).slice().reverse().forEach(e => {
                    //Check
                    if (!(this.options[e]?.isFilterable ?? false)) {
                        return;
                    }
                    let tValue = this.options[e].values.find(f => f.name == this.entryData.custom[e]);
                    {
                        if (!tValue || !tValue.possible) {
                            this.options[e].currentValue = null;
                        }
                    }
                });
                // #endregion
                // #region Info
                //Find
                let tItems1 = this.getItemsByFilter(this.items, this.entryData.selection);
                if (tItems1.length != 1) {
                    this.setCurrentItem(this.product, tItems1);
                }
                else {
                    this.setCurrentItem(tItems1[0], tItems1);
                }
                // #endregion
            }
            // #endregion        
            // #region Info
            this.OnCurrentImageChanged();
            // #endregion
        },
        // #endregion
        // #region Selector | Helper
        getValues(arg0) {
            //New
            var tdata = new Array();
            //Fill
            arg0.forEach((e) => {
                tdata.push(new ProductSelectorValue({
                    name: e.optionID,
                    caption: e.caption,
                    description: e.description,
                    note: e.note,
                    imageUrl: e.imageUrl,
                    imageAddress: e.imageAddress,
                    selected: false,
                    disabled: false,
                    possible: true,
                    configuration: e.configuration,
                    configurationValue: e.configurationValue,
                }));
            });
            return tdata;
        },
        hasConfiguration() {
            // #region Vars
            let tData = this.entryData.selection;
            // #endregion
            for (let item of Object.keys(tData)) {
                if (!tData[item].entry) {
                    continue;
                }
                if (Object.keys(tData[item].entry).length === 0) {
                    continue;
                }
                return true;
            }
            return false;
        },
        getItemsByFilter(items, filter) {
            return items.filter((e) => {
                for (var name in filter) {
                    if (utils.isNullOrEmpty(e.attributes)) {
                        return false;
                    }
                    let tValues = e.attributes[name];
                    if (utils.isArray(tValues)) {
                        if (!tValues.some(e => e == filter[name]?.value)) {
                            return false;
                        }
                    }
                    else {
                        if (tValues !== filter[name]?.value) {
                            return false;
                        }
                    }
                }
                return true;
            });
        },
        getValuesFromItems(items, member) {
            //new
            let tValues = [];
            items.forEach(e => {
                if (!e.attributes) {
                    return;
                }
                let tValue = e.attributes[member];
                if (!tValue) {
                    return;
                }
                if (Array.isArray(tValue)) {
                    tValue.forEach(f => {
                        if (tValues.includes(f)) {
                            return;
                        }
                        tValues.push(f);
                    });
                }
                else {
                    if (tValues.includes(tValue)) {
                        return;
                    }
                    tValues.push(tValue);
                }
            });
            return tValues;
        },
        getImages() {
            if (!this.entryData.selection) {
                this.entryData.selection = {};
            }
            //new
            let tData = [];
            //Fill: UserData
            Object.keys(this.options).reverse().forEach(e => {
                //Find
                let tOption = this.options[e];
                //Check
                if (!tOption.values || tOption.values.length == 0) {
                    return;
                }
                //Value
                let tSelectedValue = null;
                if (tOption.isSelection) {
                    tSelectedValue = this.entryData.selection[e]?.value;
                }
                else {
                    tSelectedValue = this.entryData.custom[e];
                }
                if (!tSelectedValue) {
                    return;
                }
                //Value
                let tOptionValue = tOption.values.find(e => e.name == tSelectedValue);
                //Option
                if (tOptionValue && tOptionValue.imageAddress) {
                    tData.push({
                        fullUrl: tOptionValue?.imageUrl ?? '',
                        previewUrl: tOptionValue?.imageUrl ?? '',
                        address: tOptionValue?.imageAddress ?? '',
                        name: ""
                    });
                }
            });
            return tData;
        },
        // #endregion
        // #region Events
        OnCurrentImageChanged() {
            this.$emit('command', 'selector_image_changed', { images: this.getImages(), items: this.selectedItems });
        },
        OnCustomItemChanged() {
            if (!this.hasConfiguration()) {
                return;
            }
            if (this.currentItem && this.currentItem.isReal) {
                this.$emit('command', 'selector_configuration_changed', { item: {} });
            }
        },
        OnCurrentItemChanged() {
            this.$emit('command', 'selector_item_changed', { item: this.currentItem, items: this.selectedItems });
        },
        // #endregion
        // #region Common
        setCurrentItemByNumber(number) {
            // #region Check
            if (utils.isNullOrEmpty(number)) {
                return;
            }
            // #endregion
            // #region Item
            //Find
            let tItem = this.items.find(e => e.itemNumber === number);
            //Run
            if (!tItem) {
                this.setCurrentItem(this.product, this.product?.variants ?? []);
            }
            else {
                this.setCurrentItem(tItem, [tItem]);
            }
            // #endregion
        }
        // #endregion
    },
    // #endregion
});
// #endregion
